import { computed, Service, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { UartOptionsCodec } from '@app/typedef/codec/uart/UartOptionsCodec';
import { sameUartOptions, UartOptions } from '@app/typedef/define/uart/UartOptions';
import { UartError, UartFrame, UartPortInfo, UartStatus } from '@app/typedef/define/uart/UartPort';

/** `localStorage` 的键。沿用仓库既有口径：裸键、没有包装类（同 `theme.service.ts` 的 `dark_mode`）。 */
const OPTIONS_KEY = 'uart.options';
const LAST_PORT_KEY = 'uart.lastPort';

/**
 * 串口连接管理。
 *
 * 职责边界**只到「端口生命周期 + 原始字节流」**：它不认识 HEX/ASCII 这些显示格式、
 * 不保存收发记录、也不懂任何上层协议。收发记录归页面自己拿 `rx$` / `tx$` 去攒 ——
 * 这样后续的「历史」页可以有它自己的保留策略与上限，不被调试页那 2000 条绑住。
 *
 * 状态用 signal、字节流用 Observable：前者是低频的、要参与变更检测的值；
 * 后者是高频事件流，需要「取消订阅」这个语义，塞进 signal 数组会导致每收一包就重建整个数组。
 *
 * **不做自动重连**：串口是独占资源，页面一打开就去 `open()` 会和其它程序或标签页抢设备，
 * 而且用户可能只是想改个参数。启动时只列出已授权的端口并标出「上次用过的那个」，由用户点连接。
 */
@Service()
export class UartService {
  /** 浏览器是否提供 Web Serial。 */
  readonly hasSerialApi: boolean = typeof navigator !== 'undefined' && 'serial' in navigator;

  /**
   * 是否为安全上下文。
   *
   * 它**不是**用来跟 `hasSerialApi` 做与运算的 —— Web Serial 本来就只在安全上下文里暴露，
   * 有 `navigator.serial` 必然安全，这个字段存在的意义是**区分没 API 的两种原因**：
   * 非安全上下文（该换 HTTPS 或 localhost 访问）还是浏览器不支持（该换 Chrome/Edge）。
   * 这两种情况给用户的下一步动作完全不同，笼统说一句「不支持」等于没说。
   */
  readonly isSecureContext: boolean = typeof isSecureContext === 'boolean' ? isSecureContext : true;

  readonly supported: boolean = this.hasSerialApi;

  /*
   * 初始状态直接落 `unsupported` —— 环境不支持时界面不该显示「未连接」，
   * 那会让用户一直去找「为什么连不上」，而真正的原因是这个浏览器根本没有这个能力。
   */
  private readonly statusSignal = signal<UartStatus>(this.supported ? 'disconnected' : 'unsupported');
  private readonly portInfoSignal = signal<UartPortInfo | null>(null);
  private readonly optionsSignal = signal<UartOptions>(loadOptions());
  private readonly errorSignal = signal<UartError | null>(null);
  private readonly grantedPortsSignal = signal<SerialPort[]>([]);
  /** 端口打开时**实际生效**的那份参数。与 `optionsSignal` 一比就知道要不要重连。 */
  private readonly appliedSignal = signal<UartOptions | null>(null);

  readonly status = this.statusSignal.asReadonly();
  readonly portInfo = this.portInfoSignal.asReadonly();
  readonly options = this.optionsSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly grantedPorts = this.grantedPortsSignal.asReadonly();

  readonly connected = computed(() => this.statusSignal() === 'connected');
  readonly busy = computed(() => this.statusSignal() === 'connecting');

  /**
   * 界面上填的参数与端口正在用的参数已经分叉 —— 要重新连接才生效。
   * Web Serial 不支持在已打开的端口上改参数（改了也只能 `close()` 再 `open()`）。
   */
  readonly needsReconnect = computed(() => {
    const applied = this.appliedSignal();
    return applied !== null && !sameUartOptions(this.optionsSignal(), applied);
  });

  private readonly rxSubject = new Subject<UartFrame>();
  private readonly txSubject = new Subject<UartFrame>();
  readonly rx$: Observable<UartFrame> = this.rxSubject.asObservable();
  readonly tx$: Observable<UartFrame> = this.txSubject.asObservable();

  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private readTask: Promise<void> | null = null;
  /** 读循环的退出开关：`disconnect()` 会先置 true，读循环据此区分「主动关」与「链路断了」。 */
  private closing = false;

  /** 上次用过的端口（按 USB VID:PID 认）。做成信号，界面上的「上次使用」标记才会跟着连接动作走。 */
  private readonly lastPortKeySignal = signal<string | null>(readLastPortKey());

  constructor() {
    if (!this.hasSerialApi) return;
    /*
     * 设备被拔掉时，真正把连接收回来的**不是**这个事件，而是读循环 ——
     * 拔线会让 `reader.read()` 抛 NetworkError，那条路径一定会走到，且能拿到确切原因。
     * 这里只负责把「已授权端口」列表刷新一下，让界面上的列表跟得上设备的插拔。
     */
    navigator.serial.addEventListener('connect', () => void this.refreshGrantedPorts());
    navigator.serial.addEventListener('disconnect', () => void this.refreshGrantedPorts());
  }

  // ---------------------------------------------------------------- 端口授权

  /** 列出已授权给本页面的端口，**不会弹窗**。 */
  async refreshGrantedPorts(): Promise<void> {
    if (!this.hasSerialApi) return;
    try {
      this.grantedPortsSignal.set(await navigator.serial.getPorts());
    } catch (e) {
      this.errorSignal.set(toUartError(e));
    }
  }

  /**
   * 弹出浏览器的端口选择框。**必须由用户手势（点击）触发**，否则浏览器抛 SecurityError。
   *
   * 用户点「取消」也会走到 catch（NotFoundError）—— 那不是错误，不报给用户。
   */
  async requestPort(): Promise<SerialPort | null> {
    if (!this.hasSerialApi) {
      this.errorSignal.set({ key: '当前浏览器不支持串口功能' });
      return null;
    }
    this.errorSignal.set(null);
    try {
      const port = await navigator.serial.requestPort();
      await this.refreshGrantedPorts();
      return port;
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'NotFoundError')) {
        this.errorSignal.set(toUartError(e));
      }
      return null;
    }
  }

  /** 撤销某个端口的授权。撤销后要重新 `requestPort()` 才能再用。 */
  async forget(port: SerialPort): Promise<void> {
    if (this.port === port) await this.disconnect();
    try {
      await port.forget();
    } catch (e) {
      this.errorSignal.set(toUartError(e));
    }
    await this.refreshGrantedPorts();
  }

  /** 该端口是不是上次用过的那一个（按 USB VID:PID 认）。 */
  isLastUsed(port: SerialPort): boolean {
    const key = portKeyOf(port.getInfo());
    return key !== null && key === this.lastPortKeySignal();
  }

  /** 该端口是不是当前打开着的那个。比身份而不是比 VID:PID —— 同型号两个模块的 ID 是一样的。 */
  isConnected(port: SerialPort): boolean {
    return this.port === port;
  }

  // ---------------------------------------------------------------- 连接

  /** 打开端口并开始接收。已连接其它端口时会先断开。 */
  async connect(port: SerialPort): Promise<void> {
    /*
     * 已经连着**同一个**端口：什么都不做。
     *
     * 不拦的话会再 `open()` 一次，抛 `InvalidStateError`，经 `toUartError` 变成
     * 「该串口已被打开，可能正被其它程序或标签页占用」—— 那句话对着用户自己刚建立的连接说，
     * 只会让人去别处找占用者。改参数要重开请走 `reconnect()`（它会先 `disconnect()`）。
     */
    if (this.port === port && this.statusSignal() === 'connected') return;

    if (this.port && this.port !== port) await this.disconnect();

    this.errorSignal.set(null);
    this.statusSignal.set('connecting');

    const options = this.optionsSignal();
    try {
      await port.open(options);
    } catch (e) {
      this.statusSignal.set('disconnected');
      this.errorSignal.set(toUartError(e));
      return;
    }

    this.port = port;
    this.closing = false;
    this.appliedSignal.set(copyOf(options));
    this.portInfoSignal.set(port.getInfo());
    this.statusSignal.set('connected');
    this.rememberPort(port);
    this.readTask = this.readLoop(port);
  }

  /** 断开并释放端口。重复调用是安全的。 */
  async disconnect(): Promise<void> {
    if (!this.port) {
      this.statusSignal.set('disconnected');
      return;
    }

    this.closing = true;
    // 先让读循环退出。reader 的锁没释放之前不能碰 port.close()，否则抛 InvalidStateError。
    const reader = this.reader;
    if (reader) {
      try {
        await reader.cancel();
      } catch {
        /* 设备已经掉了，cancel 也会失败 —— 无所谓，下面照样收尾 */
      }
    }
    const task = this.readTask;
    this.readTask = null;
    if (task) await task;

    await this.releaseResources();
  }

  // ---------------------------------------------------------------- 收发

  /** 发送一段字节。成功后才计入 TX 记录；失败只记错误，不产生「发过了」的假象。 */
  async write(data: Uint8Array): Promise<void> {
    const port = this.port;
    if (!port || this.statusSignal() !== 'connected') {
      this.errorSignal.set({ key: '串口未连接' });
      return;
    }
    try {
      const writer = this.acquireWriter(port);
      await writer.write(data);
      this.txSubject.next({ direction: 'tx', data, at: Date.now() });
    } catch (e) {
      this.errorSignal.set(toUartError(e));
    }
  }

  // ---------------------------------------------------------------- 控制线

  /**
   * 把这条线拉低一段时间再放开（BREAK）。
   *
   * 这是 DMX512 分隔帧用的那个 BREAK（见 DMX512.md 1.2：BREAK ≥ 88µs、MAB ≥ 8µs），
   * 也是**调试页上那个最小验证**的实现 —— 浏览器到底发不发得出去，只有真适配器答得了。
   *
   * `setSignals({break})` 是 Web Serial 唯一的线级接口，它**没有时长参数**：电平置高、
   * 等一段、再置低，全得靠这里自己等。所以实测时长一定长于标称值（一次跨进程往返 +
   * 定时器精度，现实里 1ms 起，见调试页那个常量），返回值给的是**实测**的断言时长
   * —— 标称值是请求，实测值才是事实。
   *
   * 三件要紧的事：
   * ① **一定要放开**。放开那一步放在 `try` 外面无条件执行：断言抛了也要放（幂等），
   *    而漏放一次，这条线就永远低着 —— 对面从此一个字节都收不到，比没发出去严重得多；
   * ② 断言与放开任一失败都当失败报，**不产生「发过了」的假象**；
   * ③ 放开失败按更严重的那一档报：线上还低着，得让人立刻知道。
   *
   * 失败只落 `errorSignal` 并返回 `null`。**这里不走 `toUartError`**：那条映射把
   * `NetworkError` 说成「设备可能已被拔出」，而 `setSignals` 失败多半是这个适配器的驱动
   * 不认 TIOCSBRK —— 两件事的处置完全不同（拔线要重连，不认就是不认）。浏览器原话照旧进 `detail`。
   *
   * @param durationMs 标称的 BREAK 时长（毫秒）
   * @returns 实测的 BREAK 时长（毫秒）；失败给 `null`
   */
  async pulseBreak(durationMs: number): Promise<number | null> {
    const port = this.port;
    if (!port || this.statusSignal() !== 'connected') {
      this.errorSignal.set({ key: '串口未连接' });
      return null;
    }

    let elapsed: number | null = null;
    let failure: unknown = null;
    const started = performance.now();
    try {
      await port.setSignals({ break: true });
      await new Promise((resolve) => setTimeout(resolve, durationMs));
      elapsed = performance.now() - started;
    } catch (e) {
      failure = e;
    }
    try {
      await port.setSignals({ break: false });
    } catch (e) {
      // 放开失败比断言失败严重：线还低着
      failure = e;
      elapsed = null;
    }
    if (failure) {
      this.errorSignal.set({ key: '这个串口不接受 BREAK', detail: describeThrown(failure) });
      return null;
    }
    return elapsed;
  }

  // ---------------------------------------------------------------- 参数

  /**
   * 改参数并持久化。
   *
   * 已连接时**不会**立刻生效 —— 由界面根据 `needsReconnect` 提示用户，用户点重连才换参数。
   * 这里要是自作主张重连，用户每调一个下拉框端口就会断一次。
   */
  setOptions(patch: Partial<UartOptions>): void {
    this.optionsSignal.update((current) => {
      const next = Object.assign(new UartOptions(), current, patch);
      saveOptions(next);
      return next;
    });
  }

  /** 参数恢复默认并持久化。 */
  resetOptions(): void {
    const next = new UartOptions();
    saveOptions(next);
    this.optionsSignal.set(next);
  }

  /** 按当前参数断开重连。 */
  async reconnect(): Promise<void> {
    const port = this.port;
    if (!port) return;
    await this.disconnect();
    await this.connect(port);
  }

  /** 清掉当前的错误提示（界面上的关闭按钮用）。 */
  clearError(): void {
    this.errorSignal.set(null);
  }

  // ---------------------------------------------------------------- 内部

  /**
   * 读循环。
   *
   * 每次 `reader.read()` 的返回值**原样**作为一条 RX 记录发出去，不做任何按分隔符的切分：
   * 串口上跑的常常是没有换行符的二进制协议（Modbus RTU、BACnet MS-TP 都是），
   * 按 `0x0a` 切行会让这类报文永远凑不齐一条记录、只字不出现在界面上。
   */
  private async readLoop(port: SerialPort): Promise<void> {
    while (!this.closing && port.readable) {
      const reader = port.readable.getReader();
      this.reader = reader;
      let linkLost = false;

      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value && value.length > 0) {
            this.rxSubject.next({ direction: 'rx', data: value, at: Date.now() });
          }
        }
      } catch (e) {
        // 拔线、端口被关 —— read() 会抛错。这不是「出错」，是链路断了。
        linkLost = !this.closing;
        if (linkLost) this.errorSignal.set(toUartError(e));
      } finally {
        // 主动断开时 disconnect() 已经释放过这一把锁了，重复释放会抛，忽略即可。
        try {
          reader.releaseLock();
        } catch {
          /* 已经释放过了 */
        }
        if (this.reader === reader) this.reader = null;
      }

      if (linkLost) break;
    }

    // 不是我们主动关的 → 链路自己断了，把状态和资源收回来，否则界面会一直停在「已连接」。
    if (!this.closing) await this.releaseResources();
  }

  /**
   * 释放 reader / writer / port 并复位状态。可重复调用。
   *
   * **不碰 `readTask`**：本方法会被读循环自己调用，在里面等自己会死锁。
   * 等读循环结束是 `disconnect()` 的活。
   */
  private async releaseResources(): Promise<void> {
    /*
     * 顺序不能反：两条流里任何一条还锁着，`port.close()` 都会抛 InvalidStateError。
     * 反过来说，把 close() 包在 try/catch 里「反正出错也忽略」，就会留下
     * 「界面显示已断开、设备其实还占着」这种最难查的状态。
     */
    const writer = this.writer;
    this.writer = null;
    if (writer) {
      try {
        await writer.close();
      } catch {
        /* 设备已经掉了 */
      }
      try {
        writer.releaseLock();
      } catch {
        /* 已经释放过了 */
      }
    }

    const reader = this.reader;
    this.reader = null;
    if (reader) {
      try {
        reader.releaseLock();
      } catch {
        /* 已经释放过了 */
      }
    }

    const port = this.port;
    this.port = null;
    if (port) {
      try {
        await port.close();
      } catch {
        /* 设备已拔出时 close() 会失败，此时已经没有别的办法了 */
      }
    }

    this.portInfoSignal.set(null);
    this.appliedSignal.set(null);
    this.statusSignal.set('disconnected');
    this.closing = false;
  }

  /**
   * 取写端。**懒获取并长期持有** —— 读/写是两条独立的流，可以并存；
   * 每次发送都 `getWriter()`/`releaseLock()` 一轮既多一层开销，也让并发发送更容易交错。
   */
  private acquireWriter(port: SerialPort): WritableStreamDefaultWriter<Uint8Array> {
    if (this.writer) return this.writer;
    const writable = port.writable;
    if (!writable) throw new DOMException('端口当前不可写', 'InvalidStateError');
    this.writer = writable.getWriter();
    return this.writer;
  }

  private rememberPort(port: SerialPort): void {
    const key = portKeyOf(port.getInfo());
    if (key === null) return;
    this.lastPortKeySignal.set(key);
    try {
      localStorage.setItem(LAST_PORT_KEY, key);
    } catch {
      /* 隐私模式下 localStorage 会抛，记不住就算了 */
    }
  }
}

// -------------------------------------------------------------------- 模块级

/** `{usbVendorId, usbProductId}` → `"6790:29987"`。没有 USB 标识（板载 UART）时返回 null。 */
function portKeyOf(info: UartPortInfo): string | null {
  return info.usbVendorId == null && info.usbProductId == null
    ? null
    : `${info.usbVendorId ?? ''}:${info.usbProductId ?? ''}`;
}

function copyOf(options: UartOptions): UartOptions {
  return Object.assign(new UartOptions(), options);
}

function loadOptions(): UartOptions {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    return raw === null ? new UartOptions() : UartOptionsCodec.decode(JSON.parse(raw));
  } catch {
    // 存储被禁用、或存进去的不是合法 JSON —— 都不该让整个应用起不来。
    return new UartOptions();
  }
}

function saveOptions(options: UartOptions): void {
  try {
    localStorage.setItem(OPTIONS_KEY, UartOptionsCodec.encode(options));
  } catch {
    /* 隐私模式下写不进去，不影响本次会话使用 */
  }
}

function readLastPortKey(): string | null {
  try {
    return localStorage.getItem(LAST_PORT_KEY);
  } catch {
    return null;
  }
}

/** 浏览器原话（`NetworkError: Failed to set control signals` 这样），给排查的人看，不翻译。 */
function describeThrown(e: unknown): string {
  if (e instanceof DOMException) {
    return `${e.name}: ${e.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * 把浏览器的报错翻成一条能拿去展示的错误。
 *
 * 这活归服务干而不是页面：页面拿到的是 `DOMException`，它没法知道
 * `InvalidStateError` 在这里意味着「端口被别的程序占了」——那是 Web Serial 的领域知识。
 *
 * `key` 只给「该怎么办」层面的结论，`detail` 保留浏览器的原话供排查。
 */
function toUartError(e: unknown): UartError {
  if (e instanceof DOMException) {
    switch (e.name) {
      case 'NotFoundError':
        return { key: '未选择串口' };
      case 'InvalidStateError':
        return { key: '该串口已被打开，可能正被其它程序或标签页占用', detail: e.message };
      case 'NetworkError':
        return { key: '串口通信失败，设备可能已被拔出', detail: e.message };
      case 'SecurityError':
        return { key: '当前页面无权访问串口，请确认通过 HTTPS 或 localhost 打开', detail: e.message };
      case 'NotAllowedError':
        return { key: '串口访问被拒绝', detail: e.message };
      default:
        return { key: '串口操作失败', detail: describeThrown(e) };
    }
  }
  return { key: '串口操作失败', detail: describeThrown(e) };
}
